import {
  sendTaskLocation,
  sendLocationTimeSeries
} from '@/app/services/dashboard'
import { getLatLong, getBrowserName } from '@/app/utils/helpers'
import { isFlutterApp, sendFlutterTimeSeriesActivity } from '@/app/utils/flutterBridge'

export const JOB_STEPS = {
  START_JOB: 'START_JOB',
  SELFIE: 'SELFIE',
  AUDIO_RECORDING: 'AUDIO_RECORDING',
  MEETING_NOTES: 'MEETING_NOTES',
  OTP_VERIFICATION: 'OTP_VERIFICATION',
  MEETING_NOTES_WITH_ATTACHMENTS: 'MEETING_NOTES_WITH_ATTACHMENTS',
  QUESTIONNAIRE: 'QUESTIONNAIRE',
  COMPLAINT_INVENTORY: 'COMPLAINT_INVENTORY',
  TASK_END: 'TASK_END',
  TASK_COMPLETED: 'TASK_COMPLETED'
}

export const STEP_ROUTES = {
  [JOB_STEPS.START_JOB]: 'AppStartJob',
  [JOB_STEPS.SELFIE]: 'AppTaskSelfie',
  [JOB_STEPS.AUDIO_RECORDING]: 'AppTaskAudio',
  [JOB_STEPS.MEETING_NOTES]: 'AppTaskMeeting',
  [JOB_STEPS.OTP_VERIFICATION]: 'AppTaskOtp',
  [JOB_STEPS.MEETING_NOTES_WITH_ATTACHMENTS]: 'AppTaskMeeting',
  [JOB_STEPS.QUESTIONNAIRE]: 'AppTaskQuestionnaire',
  [JOB_STEPS.COMPLAINT_INVENTORY]: 'AppTaskInventory',
  [JOB_STEPS.TASK_END]: 'AppTaskCompleted',
  [JOB_STEPS.TASK_COMPLETED]: 'AppTaskCompleted'
}

export const TASK_ACTIVITIES = {
  AppStartJob: 'taskStart',
  AppTaskSelfie: 'uploadSelfie',
  AppTaskAudio: 'uploadAudio',
  AppTaskMeeting: 'addMeetingNotes',
  AppTaskOtp: 'validateOTP',
  AppTaskQuestionnaire: 'questionnaire',
  AppTaskInventory: 'complaintInventory',
  AppTaskCompleted: 'taskEnded',
  // Direct step mappings
  START_JOB: 'taskStart',
  SELFIE: 'uploadSelfie',
  AUDIO_RECORDING: 'uploadAudio',
  MEETING_NOTES: 'addMeetingNotes',
  MEETING_NOTES_WITH_ATTACHMENTS: 'addMeetingNotes',
  OTP_VERIFICATION: 'validateOTP',
  QUESTIONNAIRE: 'questionnaire',
  COMPLAINT_INVENTORY: 'complaintInventory',
  TASK_END: 'taskEnded',
  TASK_COMPLETED: 'taskEnded'
}

export function getActiveUserId() {
  try {
    const raw = localStorage.getItem('user')
    const userObj = raw ? JSON.parse(raw) : null
    return (
      localStorage.getItem('user-id') ||
      userObj?._id ||
      userObj?.user?._id ||
      userObj?.id ||
      ''
    )
  } catch (e) {
    return localStorage.getItem('user-id') || ''
  }
}

export async function sendStepLocation(routeName, taskData, options = {}) {
  const { skipTimeSeries = false } = options
  let actionName = TASK_ACTIVITIES[routeName] || 'taskStart'
  if (actionName === 'taskComplete') {
    actionName = 'taskEnded'
  }
  const taskId = taskData?._id || taskData?.id
  const userId = getActiveUserId()

  let coords = { latitude: 0, longitude: 0 }
  let deviceData = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceName: getBrowserName()
  }

  // 1. Fetch location & device data (native GPS in Flutter, navigator.geolocation in browser)
  try {
    const pos = await getLatLong()
    if (pos?.coords) {
      coords = pos.coords
    }
    if (pos?.deviceData) {
      deviceData = { ...deviceData, ...pos.deviceData }
    }
  } catch (e) {
    console.warn('Geolocation warning on task step:', e)
  }

  // 2. Build Location Model payload (for LocationTracker/DailyDistanceTracker via POST /user/v2/location)
  const locationModelPayload = {
    userId,
    latitude: coords.latitude,
    longitude: coords.longitude,
    deviceData,
    activity: {
      activityType: 'TASK',
      taskDetails: taskId,
      caseTaskDetails: taskId,
      action: actionName
    }
  }

  // 3. Build Location Time Series Model payload (for LocationTimeSeries via POST /location-time-series)
  const timeSeriesPayload = {
    userId,
    location: {
      type: 'Point',
      coordinates: [coords.longitude, coords.latitude] // GeoJSON format: [longitude, latitude]
    },
    state: 'STILL',
    source: isFlutterApp() ? 'MOBILE' : 'WEB',
    deviceName: deviceData.deviceName || (isFlutterApp() ? 'Flutter Mobile App' : getBrowserName()),
    batteryLevel: typeof deviceData.batteryHealth === 'number' ? deviceData.batteryHealth : 100,
    accuracy: typeof deviceData.accuracy === 'number' ? deviceData.accuracy : 15,
    activity: {
      activityType: 'TASK',
      action: actionName,
      taskDetails: taskId,
      caseTaskDetails: taskId,
      time: new Date().toISOString()
    }
  }

  // 4. Dispatch across Flutter Mode vs Web Mode (identical to finfi_app_v2)
  if (isFlutterApp()) {
    // In Flutter: notify native background tracking service & post Location Model to backend
    const flutterTimeSeriesActivityPayload = {
      userId,
      activity: {
        activityType: 'TASK',
        taskDetails: taskId,
        caseTaskDetails: taskId,
        action: actionName
      }
    }
    sendFlutterTimeSeriesActivity(flutterTimeSeriesActivityPayload)

    await sendTaskLocation(locationModelPayload).catch((err) =>
      console.warn('Task step location model logging warning in Flutter mode:', err)
    )
  } else {
    // In Web: post Location Model & post Location Time Series Model
    try {
      const promises = [sendTaskLocation(locationModelPayload)]
      if (!skipTimeSeries && coords.latitude && coords.longitude && userId) {
        promises.push(sendLocationTimeSeries(timeSeriesPayload))
      }
      await Promise.allSettled(promises)
    } catch (err) {
      console.warn('Failed to send task location logs:', err)
    }
  }
}

export async function postStepLocationTimeSeries(routeName, taskData, options = {}) {
  let actionName = TASK_ACTIVITIES[routeName] || 'uploadSelfie'
  if (actionName === 'taskComplete') {
    actionName = 'taskEnded'
  }
  const taskId =
    taskData?._id ||
    taskData?.taskId ||
    taskData?.case?.tasks?.[0]?._id ||
    taskData?.case?._id ||
    taskData?.id ||
    (typeof window !== 'undefined' && (window.task?.value?._id || window.task?.value?.taskId))
  const userId = getActiveUserId()

  let coords = options.coords || { latitude: 0, longitude: 0 }
  let deviceData = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceName: getBrowserName()
  }

  if (!coords.latitude && !coords.longitude) {
    try {
      const pos = await getLatLong()
      if (pos?.coords) {
        coords = pos.coords
      }
      if (pos?.deviceData) {
        deviceData = { ...deviceData, ...pos.deviceData }
      }
    } catch (e) {
      console.warn('Geolocation warning on task step:', e)
    }
  }

  const timeSeriesPayload = {
    userId,
    location: {
      type: 'Point',
      coordinates: [coords.longitude, coords.latitude]
    },
    state: 'STILL',
    source: isFlutterApp() ? 'MOBILE' : 'WEB',
    deviceName: deviceData.deviceName || (isFlutterApp() ? 'Flutter Mobile App' : getBrowserName()),
    batteryLevel: typeof deviceData.batteryHealth === 'number' ? deviceData.batteryHealth : 100,
    accuracy: typeof deviceData.accuracy === 'number' ? deviceData.accuracy : 15,
    activity: {
      activityType: 'TASK',
      action: actionName,
      taskDetails: taskId,
      caseTaskDetails: taskId,
      time: new Date().toISOString()
    }
  }

  try {
    const res = await sendLocationTimeSeries(timeSeriesPayload)
    const locData = res?.data?.data || res?.data
    const address =
      locData?.location?.address ||
      locData?.address ||
      res?.data?.location?.address ||
      res?.data?.address ||
      ''
    const coordinates =
      locData?.location?.coordinates ||
      locData?.coordinates ||
      res?.data?.location?.coordinates ||
      null
    return {
      success: true,
      address,
      coordinates,
      data: locData
    }
  } catch (err) {
    console.warn('Location time series call failed:', err)
    return {
      success: false,
      address: '',
      coordinates: null,
      error: err
    }
  }
}

export async function recordCustomActivity(action) {
  const userId = getActiveUserId()

  let coords = { latitude: 0, longitude: 0 }
  let deviceData = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceName: getBrowserName()
  }

  // 1. Fetch location & device data (native GPS in Flutter, navigator.geolocation in browser)
  try {
    const pos = await getLatLong()
    if (pos?.coords) {
      coords = pos.coords
    }
    if (pos?.deviceData) {
      deviceData = { ...deviceData, ...pos.deviceData }
    }
  } catch (e) {
    console.warn('Geolocation warning on custom activity:', e)
  }

  // 2. Build Location Model payload (POST /user/v2/location)
  const locationModelPayload = {
    userId,
    latitude: coords.latitude,
    longitude: coords.longitude,
    deviceData,
    activity: {
      activityType: 'CUSTOM',
      action
    }
  }

  // 3. Build Location Time Series Model payload (POST /location-time-series)
  const timeSeriesPayload = {
    userId,
    location: {
      type: 'Point',
      coordinates: [coords.longitude, coords.latitude]
    },
    state: 'STILL',
    source: isFlutterApp() ? 'MOBILE' : 'WEB',
    deviceName: deviceData.deviceName || (isFlutterApp() ? 'Flutter Mobile App' : getBrowserName()),
    batteryLevel: typeof deviceData.batteryHealth === 'number' ? deviceData.batteryHealth : 100,
    accuracy: typeof deviceData.accuracy === 'number' ? deviceData.accuracy : 15,
    activity: {
      activityType: 'CUSTOM',
      action,
      time: new Date().toISOString()
    }
  }

  if (isFlutterApp()) {
    const flutterTimeSeriesPayload = {
      userId,
      activity: {
        activityType: 'CUSTOM',
        action
      }
    }
    sendFlutterTimeSeriesActivity(flutterTimeSeriesPayload)
    return await sendTaskLocation(locationModelPayload).catch((err) =>
      console.warn('Custom activity location model logging warning in Flutter mode:', err)
    )
  } else {
    try {
      const promises = [sendTaskLocation(locationModelPayload)]
      if (coords.latitude && coords.longitude && userId) {
        promises.push(sendLocationTimeSeries(timeSeriesPayload))
      }
      return await Promise.allSettled(promises)
    } catch (err) {
      console.warn('Failed to send custom activity location logs:', err)
    }
  }
}

export function computeTaskSteps(taskSettings, orgConfig) {
  const stepsConfig =
    taskSettings?.taskCompletionStepsConfig ||
    taskSettings?.data?.taskCompletionStepsConfig ||
    taskSettings?.taskSettings?.taskCompletionStepsConfig ||
    orgConfig?.taskCompletionStepsConfig ||
    orgConfig?.config?.taskCompletionStepsConfig

  if (!stepsConfig || !Array.isArray(stepsConfig) || !stepsConfig.length) {
    // Default flow only if no custom steps configured in org
    return [
      { type: JOB_STEPS.START_JOB, routeName: STEP_ROUTES[JOB_STEPS.START_JOB], canSkip: false },
      { type: JOB_STEPS.SELFIE, routeName: STEP_ROUTES[JOB_STEPS.SELFIE], canSkip: false },
      { type: JOB_STEPS.QUESTIONNAIRE, routeName: STEP_ROUTES[JOB_STEPS.QUESTIONNAIRE], canSkip: false },
      { type: JOB_STEPS.MEETING_NOTES, routeName: STEP_ROUTES[JOB_STEPS.MEETING_NOTES], canSkip: false },
      { type: JOB_STEPS.TASK_COMPLETED, routeName: STEP_ROUTES[JOB_STEPS.TASK_COMPLETED], canSkip: false }
    ]
  }

  const configuredSteps = stepsConfig.map((s) => ({
    type: s.step,
    routeName: STEP_ROUTES[s.step] || 'AppTaskCompleted',
    canSkip: !!s.canSkip
  }))

  return [
    { type: JOB_STEPS.START_JOB, routeName: STEP_ROUTES[JOB_STEPS.START_JOB], canSkip: false },
    ...configuredSteps,
    { type: JOB_STEPS.TASK_COMPLETED, routeName: STEP_ROUTES[JOB_STEPS.TASK_COMPLETED], canSkip: false }
  ]
}

export function getNextStepRoute(currentRouteName, stepsList, taskData) {
  const currentIndex = stepsList.findIndex((s) => s.routeName === currentRouteName)
  if (currentIndex === -1 || currentIndex >= stepsList.length - 1) {
    return 'AppTaskCompleted'
  }

  for (let i = currentIndex + 1; i < stepsList.length; i++) {
    const step = stepsList[i]

    // 1. Selfie check: skip if already uploaded
    if (step.type === JOB_STEPS.SELFIE && taskData?.additionalInfo?.selfie) {
      continue
    }

    // 2. Audio recording check: skip if already uploaded
    if (step.type === JOB_STEPS.AUDIO_RECORDING && taskData?.additionalInfo?.audio) {
      continue
    }

    // 3. Questionnaire check: skip if already submitted with answers
    const questions = taskData?.meetingDetails?.questionnaire || []
    if (step.type === JOB_STEPS.QUESTIONNAIRE && questions.some((q) => !!q.userResponse)) {
      continue
    }

    // 4. Meeting notes check: skip if already submitted
    if (step.type === JOB_STEPS.MEETING_NOTES && taskData?.meetingDetails?.meetingNotes) {
      continue
    }

    return step.routeName
  }

  return 'AppTaskCompleted'
}
